import React, { useState, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { theme } from '../../theme';

interface InputProps {
  label?: string;
  error?: string;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  type?: 'text' | 'email' | 'password' | 'tel' | 'url' | 'date' | 'number';
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  name?: string;
  id?: string;
  dark?: boolean; // For use in modals with white backgrounds
  readOnly?: boolean;
  max?: string | number;
  min?: string | number;
}

const InputContainer = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
`;

const Label = styled.label<{ required?: boolean; dark?: boolean }>`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${({ dark }) => dark ? theme.colors.light.text.primary : theme.colors.white};
  margin-bottom: ${theme.spacing[1]};

  ${({ required }) =>
    required &&
    css`
      &::after {
        content: ' *';
        color: ${theme.colors.accent.danger};
      }
    `}
`;

const StyledInput = styled.input<{
  inputSize?: 'sm' | 'md' | 'lg';
  error?: boolean;
  disabled?: boolean;
  hasPasswordToggle?: boolean;
  dark?: boolean;
  readOnly?: boolean;
}>`
  width: 100%;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.base};
  color: ${({ dark }) => dark ? '#000000' : theme.colors.white};
  background-color: ${({ dark }) => dark ? theme.colors.white : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${({ error, dark }) =>
    error ? theme.colors.accent.danger : (dark ? theme.colors.light.border.medium : 'rgba(255, 255, 255, 0.2)')};
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s ease-in-out;
  backdrop-filter: blur(10px);

  ${({ inputSize = 'md', hasPasswordToggle }) => {
    const rightPadding = hasPasswordToggle ? '40px' : theme.spacing[4];
    switch (inputSize) {
      case 'sm':
        return css`
          padding: ${theme.spacing[2]} ${rightPadding} ${theme.spacing[2]} ${theme.spacing[3]};
          font-size: ${theme.typography.fontSize.sm};
          min-height: 32px;
        `;
      case 'lg':
        return css`
          padding: ${theme.spacing[4]} ${rightPadding} ${theme.spacing[4]} ${theme.spacing[4]};
          font-size: ${theme.typography.fontSize.lg};
          min-height: 48px;
        `;
      default:
        return css`
          padding: ${theme.spacing[3]} ${rightPadding} ${theme.spacing[3]} ${theme.spacing[4]};
          font-size: ${theme.typography.fontSize.base};
          min-height: 40px;
        `;
    }
  }}

  ${({ disabled, readOnly }) =>
    (disabled || readOnly) &&
    css`
      background-color: ${readOnly ? theme.colors.light.background.tertiary : theme.colors.secondary[100]};
      color: ${readOnly ? theme.colors.light.text.secondary : theme.colors.dark.text.muted};
      cursor: ${readOnly ? 'default' : 'not-allowed'};
    `}

  &:focus {
    outline: none;
    border-color: ${({ error }) => (error ? theme.colors.accent.danger : theme.colors.primary[500])};
    box-shadow: 0 0 0 3px
      ${({ error }) =>
    error ? `${theme.colors.accent.danger}20` : `${theme.colors.primary[500]}20`};
    background-color: ${({ dark }) => dark ? theme.colors.white : 'rgba(255, 255, 255, 0.15)'};
  }

  &:hover:not(:disabled):not([readonly]) {
    border-color: ${({ error, dark }) => (error ? theme.colors.accent.danger : (dark ? theme.colors.light.border.dark : 'rgba(255, 255, 255, 0.3)'))};
    background-color: ${({ dark }) => dark ? theme.colors.white : 'rgba(255, 255, 255, 0.12)'};
  }

  &::placeholder {
    color: ${({ dark }) => dark ? theme.colors.light.text.muted : 'rgba(255, 255, 255, 0.6)'};
  }
`;

const ErrorText = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.accent.danger};
  margin-top: ${theme.spacing[1]};
`;

const HelperText = styled.span<{ dark?: boolean }>`
  font-size: ${theme.typography.fontSize.sm};
  color: ${({ dark }) => dark ? theme.colors.accent.success : 'rgba(255, 255, 255, 0.7)'};
  margin-top: ${theme.spacing[1]};
`;

const InputWrapper = styled.div<{ inputSize?: 'sm' | 'md' | 'lg' }>`
  position: relative;
  width: 100%;
`;

const PasswordToggle = styled.button<{ inputSize?: 'sm' | 'md' | 'lg'; dark?: boolean }>`
  position: absolute;
  right: ${theme.spacing[3]};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ dark }) => dark ? theme.colors.light.text.muted : 'rgba(255, 255, 255, 0.7)'};
  font-size: ${({ inputSize = 'md' }) => {
    switch (inputSize) {
      case 'sm':
        return theme.typography.fontSize.sm;
      case 'lg':
        return theme.typography.fontSize.lg;
      default:
        return theme.typography.fontSize.base;
    }
  }};
  padding: ${theme.spacing[1]};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease-in-out;
  z-index: 1;

  &:hover {
    color: ${({ dark }) => dark ? theme.colors.light.text.primary : theme.colors.white};
  }

  &:focus {
    outline: none;
    color: ${({ dark }) => dark ? theme.colors.light.text.primary : theme.colors.white};
  }
`;

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helperText,
  size = 'md',
  fullWidth = false,
  required = false,
  type = 'text',
  dark = false,
  readOnly = false,
  max,
  min,
  onKeyDown,
  ...props
}, ref) => {
  const inputId = props.id || props.name;
  const isPassword = type === 'password';
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <InputContainer fullWidth={fullWidth}>
      {label && (
        <Label htmlFor={inputId} required={required} dark={dark}>
          {label}
        </Label>
      )}
      <InputWrapper inputSize={size}>
        <StyledInput
          {...props}
          ref={ref}
          type={isPassword && showPassword ? 'text' : type}
          id={inputId}
          inputSize={size}
          error={!!error}
          hasPasswordToggle={isPassword}
          dark={dark}
          readOnly={readOnly}
          max={max !== undefined ? String(max) : undefined}
          min={min !== undefined ? String(min) : undefined}
          onKeyDown={onKeyDown}
          aria-invalid={!!error}
          aria-describedby={
            error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
          }
        />
        {isPassword && (
          <PasswordToggle
            type="button"
            onClick={togglePasswordVisibility}
            inputSize={size}
            dark={dark}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            tabIndex={-1}
          >
            {showPassword ? '👁️' : '👁️‍🗨️'}
          </PasswordToggle>
        )}
      </InputWrapper>
      {error && (
        <ErrorText id={`${inputId}-error`} role="alert">
          {error}
        </ErrorText>
      )}
      {helperText && !error && (
        <HelperText id={`${inputId}-helper`} dark={dark}>
          {helperText}
        </HelperText>
      )}
    </InputContainer>
  );
});

Input.displayName = 'Input';
